import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Menu,
  MenuItem,
  Container,
  Divider,
  Paper,
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  BottomNavigation,
  BottomNavigationAction,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import LogoutIcon from '@mui/icons-material/Logout';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CollectionsIcon from '@mui/icons-material/Collections';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { alpha } from '@mui/material/styles';
import useStore from '../store/useStore';
import CherryMark from './CherryMark';
import { orchard } from '../theme';
import { Fade, Tooltip, Chip } from '@mui/material';
import { translations } from '../translations';
import TranslateIcon from '@mui/icons-material/Translate';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import TemplateIcon from '@mui/icons-material/Description';
import TodayIcon from '@mui/icons-material/Today';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SchoolIcon from '@mui/icons-material/School';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import LibraryMusicOutlinedIcon from '@mui/icons-material/LibraryMusicOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import PersonPhotos from './photos/PersonPhotos';
import AndroidIcon from '@mui/icons-material/Android';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import PublicIcon from '@mui/icons-material/Public';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import AppUpdateBanner, { UpdateDialog } from './app/AppUpdateBanner';
import { useAppUpdate } from './app/appVersion';
import TelegramIcon from '@mui/icons-material/Telegram';
import TelegramDialog from './telegram/TelegramDialog';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CoachMark from './learn/CoachMark';
import { WelcomeGate } from './learn/WelcomeDialog';
import { HELP_BY_PATH } from './learn/lessons';

export default function Layout({ children, title }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allStudents, setAllStudents] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [overdueTaskCount, setOverdueTaskCount] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0); // packets with news (/inbox)
  const [tgOpen, setTgOpen] = useState(false); // «Telegram»: link the teacher's own chat with the bot
  const [tgLinked, setTgLinked] = useState(null);
  const [roleSwitching, setRoleSwitching] = useState(false);
  // inside the Android app: is there a newer version on the site?
  const app = useAppUpdate();
  const [updateOpen, setUpdateOpen] = useState(false);
  const {
    userRole,
    roles,
    user,
    logout,
    setSocket,
    language,
    setLanguage,
    darkMode,
    setDarkMode,
    switchRole,
    roleNotice,
    setRoleNotice,
  } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const t = translations[language];

  useEffect(() => {
    if (userRole === 'teacher' || userRole === 'admin') {
      fetch('/api/tasks/overdue-count', { credentials: 'include' })
        .then((r) => r.json())
        .then((d) => setOverdueTaskCount(d.count || 0))
        .catch(() => {});
    }
  }, [userRole]);

  // «Входящие»: how many packets have news; again after each notification
  const loadInbox = React.useCallback(() => {
    if (userRole !== 'teacher' && userRole !== 'admin') return;
    fetch('/api/packets/unread', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setInboxUnread(d.total || 0))
      .catch(() => {});
  }, [userRole]);
  useEffect(() => {
    loadInbox();
    const t = setInterval(loadInbox, 120000);
    return () => clearInterval(t);
  }, [loadInbox, location.pathname]);
  const loadInboxRef = React.useRef(loadInbox);
  loadInboxRef.current = loadInbox;
  useEffect(() => {
    if (userRole !== 'teacher' && userRole !== 'admin') return;
    fetch('/api/telegram/me', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setTgLinked(d.linked))
      .catch(() => {});
  }, [userRole]);

  useEffect(() => {
    loadNotifications();
    const socket = io(window.location.origin);
    setSocket(socket);
    if (user && user._id) {
      socket.emit('join', user._id);
    }
    // the server sends each new notification (with its id) to this user's room
    const handleNewNotification = (notification) => {
      if (notification && /^\/packet\//.test(notification.link || ''))
        loadInboxRef.current();
      setNotifications((prev) =>
        notification._id && prev.some((n) => n._id === notification._id)
          ? prev
          : [notification, ...prev]
      );
    };
    socket.on('notification', handleNewNotification);
    return () => {
      socket.disconnect();
      setSocket(null);
    };
  }, [user]);

  const loadNotifications = () => {
    fetch('/api/analytics/notifications', { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        // newest first (the server sorts and keeps the last 100)
        if (Array.isArray(data)) setNotifications(data);
      })
      .catch((err) => console.error('Error loading notifications:', err));
  };

  const handleLogout = async () => {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    logout();
    navigate('/login');
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ru' : 'en');
  };

  const canSwitchRole =
    Array.isArray(roles) &&
    roles.includes('teacher') &&
    roles.includes('admin');

  const handleRoleSwitch = async (_event, nextRole) => {
    // ToggleButtonGroup reports null when the active button is clicked again
    if (!nextRole || nextRole === userRole || roleSwitching) return;
    setRoleSwitching(true);
    try {
      const active = await switchRole(nextRole);
      setRoleNotice({
        severity: 'success',
        text:
          active === 'admin'
            ? language === 'en'
              ? 'Mode: Administrator'
              : 'Режим: Администратор'
            : language === 'en'
              ? 'Mode: Teacher'
              : 'Режим: Учитель',
      });
      // App.js picks the dashboard by the active role
      navigate('/');
    } catch (err) {
      setRoleNotice({ severity: 'error', text: err.message });
    } finally {
      setRoleSwitching(false);
    }
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleNotificationClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n._id === id ? { ...n, read: true } : n))
    );

    fetch(`/api/analytics/notifications/${id}/read`, {
      method: 'PATCH',
      credentials: 'include',
    }).catch(() => {});
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    fetch('/api/analytics/notifications/read-all', {
      method: 'PATCH',
      credentials: 'include',
    }).catch(() => {});
  };

  const handleClearRead = () => {
    setNotifications((prev) => prev.filter((n) => !n.read));
    fetch('/api/analytics/notifications/read', {
      method: 'DELETE',
      credentials: 'include',
    }).catch(() => {});
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const openSearch = () => {
    setSearchOpen(true);
    setSearchQuery('');
    if (allStudents.length === 0) {
      fetch('/api/students', { credentials: 'include' })
        .then((r) => r.json())
        .then((d) => setAllStudents(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
    if (allTemplates.length === 0) {
      fetch('/api/templates', { credentials: 'include' })
        .then((r) => r.json())
        .then((d) => setAllTemplates(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
  };

  // Keyboard shortcut Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const q = searchQuery.toLowerCase();
  const filteredStudents = q
    ? allStudents.filter((s) => s.name?.toLowerCase().includes(q))
    : [];
  const filteredTemplates = q
    ? allTemplates.filter((t) => t.name?.toLowerCase().includes(q))
    : [];

  const menuItems = [{ text: t.dashboard, icon: <DashboardIcon />, path: '/' }];

  if (userRole === 'admin' || userRole === 'teacher') {
    menuItems.push({
      text: language === 'en' ? 'Learn Core' : 'Обучение',
      icon: <SchoolOutlinedIcon sx={{ color: orchard.honey }} />,
      path: '/learn',
    });
    menuItems.push({
      text: language === 'en' ? 'Today' : 'Сегодня',
      icon: <TodayIcon />,
      path: '/today',
    });
    menuItems.push({
      text: language === 'en' ? 'Calendar' : 'Календарь',
      icon: <EventNoteOutlinedIcon />,
      path: '/calendar',
    });
    menuItems.push({
      text: language === 'en' ? 'Documents' : 'Документы',
      icon: <ArticleOutlinedIcon />,
      path: '/documents',
    });
    menuItems.push({
      text: language === 'en' ? 'Inbox' : 'Входящие',
      icon: (
        <Badge badgeContent={inboxUnread || null} color="error" max={9}>
          <MoveToInboxOutlinedIcon />
        </Badge>
      ),
      path: '/inbox',
    });
    menuItems.push({
      text: language === 'en' ? 'Pictures' : 'Картинки',
      icon: <CollectionsOutlinedIcon />,
      path: '/images',
    });
    menuItems.push({
      text: language === 'en' ? 'Songs' : 'Песни',
      icon: <LibraryMusicOutlinedIcon />,
      path: '/songs',
    });
    menuItems.push({
      text: language === 'en' ? 'Salary' : 'Зарплата',
      icon: <PaymentsOutlinedIcon />,
      path: '/salary',
    });
    menuItems.push({
      text: t.templates,
      icon: <DescriptionIcon />,
      path: '/templates',
    });
    menuItems.push({
      text: t.analytics,
      icon: <DashboardIcon />,
      path: '/analytics',
    });
    menuItems.push({
      text: t.resources,
      icon: <FolderIcon />,
      path: '/resources',
    });
    menuItems.push({
      text: t.videos,
      icon: <VideoLibraryIcon />,
      path: '/videos',
    });
    menuItems.push({
      text: language === 'en' ? 'Dictionary' : 'Словарь',
      icon: <CollectionsIcon />,
      path: '/dictionary',
    });
    menuItems.push({
      text: language === 'en' ? 'AI Generator' : 'AI-генератор',
      icon: <AutoAwesomeIcon sx={{ color: orchard.honey }} />,
      path: '/ai',
    });
    menuItems.push({
      text: language === 'en' ? 'AI Assistant' : 'AI-помощник',
      icon: <SmartToyIcon sx={{ color: orchard.honey }} />,
      path: '/assistant',
    });
    menuItems.push({
      text: language === 'en' ? 'Talk to Cherry' : 'Разговор с Черри',
      icon: <RecordVoiceOverRoundedIcon sx={{ color: orchard.cherry }} />,
      path: '/talk',
    });
    menuItems.push({
      text: language === 'en' ? 'Tasks' : 'Задачи',
      icon: (
        <Badge badgeContent={overdueTaskCount || null} color="error" max={9}>
          <ChecklistIcon />
        </Badge>
      ),
      path: '/tasks',
    });
    menuItems.push({
      text: language === 'en' ? 'Memory' : 'Память',
      icon: <PsychologyOutlinedIcon sx={{ color: orchard.honey }} />,
      path: '/memory',
    });
    menuItems.push({
      text: language === 'en' ? 'Public links' : 'Публичные ссылки',
      icon: <PublicIcon />,
      path: '/links',
    });
  }
  if (userRole === 'admin') {
    menuItems.push({
      text: language === 'en' ? 'AI costs' : 'Расходы AI',
      icon: <PriceCheckOutlinedIcon />,
      path: '/ai-costs',
    });
    menuItems.push({
      text: language === 'en' ? 'Settings' : 'Настройки',
      icon: <SettingsIcon />,
      path: '/settings',
    });
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(22, 16, 32, 0.82)'
              : 'rgba(251, 248, 245, 0.82)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="primary"
            aria-label="menu"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 1.5 }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              mr: 1.25,
              lineHeight: 0,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <CherryMark size={30} />
          </Box>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              fontFamily: orchard.fontDisplay,
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '1.02rem' },
              color: 'text.primary',
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Cherry-Pick{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                CORE
              </Box>
            </Box>
            {user?.username === 'demo' && (
              <Chip
                label="ДЕМО"
                size="small"
                sx={{
                  bgcolor: orchard.honey,
                  color: '#241E2B',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  height: 20,
                  fontSize: '0.65rem',
                }}
              />
            )}
          </Typography>

          {canSwitchRole && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={userRole}
              onChange={handleRoleSwitch}
              aria-label={language === 'en' ? 'Work mode' : 'Режим работы'}
              sx={{
                mr: { xs: 0.5, sm: 1 },
                flexShrink: 0,
                p: '3px',
                gap: '2px',
                borderRadius: 999,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                opacity: roleSwitching ? 0.6 : 1,
                pointerEvents: roleSwitching ? 'none' : 'auto',
                transition: 'opacity 0.2s',
                '& .MuiToggleButtonGroup-grouped': {
                  m: 0,
                  border: 0,
                  borderRadius: '999px !important',
                  px: { xs: 0.9, sm: 1.4 },
                  py: 0.45,
                  gap: 0.6,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  lineHeight: 1.2,
                  color: 'text.secondary',
                  '& .MuiSvgIcon-root': { fontSize: 18 },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: (theme) =>
                      `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                },
              }}
            >
              <Tooltip
                title={language === 'en' ? 'Teacher mode' : 'Режим учителя'}
              >
                <ToggleButton
                  value="teacher"
                  aria-label={language === 'en' ? 'Teacher' : 'Учитель'}
                >
                  <SchoolIcon />
                  <Box
                    component="span"
                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                  >
                    {language === 'en' ? 'Teacher' : 'Учитель'}
                  </Box>
                </ToggleButton>
              </Tooltip>
              <Tooltip
                title={
                  language === 'en'
                    ? 'Administrator mode'
                    : 'Режим администратора'
                }
              >
                <ToggleButton
                  value="admin"
                  aria-label={language === 'en' ? 'Admin' : 'Админ'}
                >
                  <AdminPanelSettingsIcon />
                  <Box
                    component="span"
                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                  >
                    {language === 'en' ? 'Admin' : 'Админ'}
                  </Box>
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          )}

          <Tooltip
            title={
              language === 'en' ? 'Switch to Russian' : 'Switch to English'
            }
          >
            <IconButton
              color="primary"
              onClick={toggleLanguage}
              sx={{ mr: 0.5 }}
            >
              <TranslateIcon />
              <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 800 }}>
                {language.toUpperCase()}
              </Typography>
            </IconButton>
          </Tooltip>

          <Tooltip title={darkMode ? 'Light mode' : 'Dark mode'}>
            <IconButton
              color="primary"
              onClick={() => setDarkMode(!darkMode)}
              sx={{ mr: 0.5 }}
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {(userRole === 'teacher' || userRole === 'admin') && (
            <Tooltip
              title={
                language === 'en' ? 'How does this work?' : 'Как это работает?'
              }
            >
              <IconButton
                color="primary"
                aria-label={
                  language === 'en'
                    ? 'How does this work?'
                    : 'Как это работает?'
                }
                onClick={() => {
                  const key = HELP_BY_PATH[location.pathname];
                  navigate(key ? `/learn?open=${key}` : '/learn');
                }}
                sx={{ mr: 0.5 }}
              >
                <HelpOutlineRoundedIcon />
              </IconButton>
            </Tooltip>
          )}

          {(userRole === 'teacher' || userRole === 'admin') && (
            <Tooltip
              title={language === 'en' ? 'Search students' : 'Поиск учеников'}
            >
              <IconButton
                color="primary"
                onClick={openSearch}
                sx={{ mr: 0.5, display: { xs: 'none', md: 'inline-flex' } }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )}

          <IconButton
            color="primary"
            onClick={handleNotificationClick}
            sx={{
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.1)' },
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleNotificationClose}
            PaperProps={{
              sx: {
                width: 340,
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: 460,
                mt: 1.5,
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mr: 'auto' }}
              >
                {language === 'en' ? 'Notifications' : 'Уведомления'}
              </Typography>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllRead}>
                  {language === 'en' ? 'Mark all read' : 'Прочитать все'}
                </Button>
              )}
              {unreadCount === 0 && notifications.length > 0 && (
                <Button size="small" color="inherit" onClick={handleClearRead}>
                  {language === 'en' ? 'Clear' : 'Очистить'}
                </Button>
              )}
            </Box>
            <Divider />
            {notifications.length === 0 ? (
              <MenuItem onClick={handleNotificationClose}>
                <Typography variant="body2" color="text.secondary">
                  {language === 'en' ? 'No notifications' : 'Уведомлений нет'}
                </Typography>
              </MenuItem>
            ) : (
              notifications.slice(0, 50).map((n, idx) => (
                <MenuItem
                  key={n._id || idx}
                  onClick={() => {
                    if (n._id) handleMarkAsRead(n._id);
                    handleNotificationClose();
                    if (n.link) navigate(n.link);
                  }}
                  sx={{
                    whiteSpace: 'normal',
                    py: 1.5,
                    bgcolor: n.read ? 'transparent' : 'action.hover',
                    borderLeft: n.read ? 'none' : `4px solid ${orchard.cherry}`,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {n.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {n.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {new Date(n.createdAt).toLocaleString(
                        language === 'en' ? 'en-GB' : 'ru-RU',
                        {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        }
                      )}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            border: 'none',
            boxShadow: '10px 0 40px rgba(0,0,0,0.05)',
          },
        }}
      >
        <Box sx={{ p: 3, mb: 1, mt: 8 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3.5,
              position: 'relative',
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${orchard.cherry} 0%, ${orchard.cherryDeep} 90%)`,
              color: 'white',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -30,
                right: -30,
                width: 110,
                height: 110,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                position: 'relative',
                zIndex: 1,
              }}
            >
              {(userRole === 'teacher' || userRole === 'admin') && (
                <PersonPhotos
                  kind="teacher"
                  id="me"
                  name={user?.displayName || user?.name || user?.username}
                  canEdit
                  size={52}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 900, lineHeight: 1.2 }}
                >
                  {user?.name || user?.username}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.8,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  {userRole === 'admin'
                    ? t.admin
                    : userRole === 'teacher'
                      ? t.teacher
                      : t.parent}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <List sx={{ px: 2 }}>
          {menuItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    setDrawerOpen(false);
                  }}
                  sx={{
                    py: 1.4,
                    borderRadius: 3,
                    position: 'relative',
                    bgcolor: (theme) =>
                      active
                        ? alpha(theme.palette.primary.main, 0.09)
                        : 'transparent',
                    color: active ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.09),
                      color: 'primary.main',
                    },
                    transition: '0.2s all ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 6,
                      top: '26%',
                      bottom: '26%',
                      width: 3.5,
                      borderRadius: 4,
                      bgcolor: 'primary.main',
                      opacity: active ? 1 : 0,
                      transition: 'opacity 0.2s ease',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: active ? 'primary.main' : 'inherit',
                      minWidth: 45,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: active ? 800 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          <Divider sx={{ my: 2, mx: 2 }} />
          {app.installed && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  if (app.outdated) setUpdateOpen(true);
                  else app.check(true);
                }}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  bgcolor: app.outdated
                    ? (th) => alpha(th.palette.primary.main, 0.08)
                    : undefined,
                }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}>
                  <Badge color="error" variant="dot" invisible={!app.outdated}>
                    <SystemUpdateAltRoundedIcon
                      color={app.outdated ? 'primary' : 'inherit'}
                    />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={
                    app.outdated
                      ? language === 'en'
                        ? `Update the app to ${app.latest.version}`
                        : `Обновить приложение до ${app.latest.version}`
                      : language === 'en'
                        ? `App version ${app.installed}`
                        : `Приложение, версия ${app.installed}`
                  }
                  secondary={
                    app.outdated
                      ? language === 'en'
                        ? `You have ${app.installed}`
                        : `У вас ${app.installed}`
                      : app.latest
                        ? language === 'en'
                          ? 'The latest version'
                          : 'Последняя версия'
                        : language === 'en'
                          ? 'Tap to check for updates'
                          : 'Нажмите, чтобы проверить обновления'
                  }
                  primaryTypographyProps={{
                    fontWeight: app.outdated ? 700 : 600,
                    fontSize: '0.95rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}
          {(userRole === 'teacher' || userRole === 'admin') && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  setDrawerOpen(false);
                  setTgOpen(true);
                }}
                sx={{ py: 1.5, borderRadius: 3 }}
              >
                <ListItemIcon sx={{ minWidth: 45 }}>
                  <TelegramIcon sx={{ color: '#229ED9' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Telegram"
                  secondary={
                    tgLinked
                      ? language === 'en'
                        ? 'Linked — Черри answers there'
                        : 'Привязан — Черри отвечает там'
                      : language === 'en'
                        ? 'Talk to Черри in Telegram'
                        : 'Говорить с Черри в Telegram'
                  }
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '0.95rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}
          {(userRole === 'teacher' || userRole === 'admin') &&
            /Android/i.test(navigator.userAgent) &&
            !/CherryPickApp\//.test(navigator.userAgent) && (
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  component="a"
                  href="/app/CherryPickCore.apk"
                  sx={{ py: 1.5, borderRadius: 3 }}
                >
                  <ListItemIcon sx={{ minWidth: 45 }}>
                    <AndroidIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      language === 'en'
                        ? 'Android app'
                        : 'Приложение для Android'
                    }
                    secondary={
                      language === 'en'
                        ? 'Records the screen with your face'
                        : 'Запись экрана с вашим лицом'
                    }
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.95rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                py: 1.5,
                borderRadius: 3,
                color: 'error.main',
                '&:hover': { bgcolor: 'error.50' },
              }}
            >
              <ListItemIcon sx={{ color: 'error.main', minWidth: 45 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary={t.signOut}
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <AppUpdateBanner en={language === 'en'} />
      {(userRole === 'teacher' || userRole === 'admin') && <CoachMark />}
      {(userRole === 'teacher' || userRole === 'admin') && <WelcomeGate />}
      {(userRole === 'teacher' || userRole === 'admin') && (
        <TelegramDialog
          open={tgOpen}
          onClose={() => setTgOpen(false)}
          onChange={setTgLinked}
        />
      )}
      {app.outdated && (
        <UpdateDialog
          open={updateOpen}
          onClose={() => setUpdateOpen(false)}
          latest={app.latest}
          installed={app.installed}
          en={language === 'en'}
        />
      )}
      <Fade in={true} timeout={600}>
        <Container
          maxWidth="lg"
          className="cp-stagger"
          sx={{ mt: 4, mb: 4, pb: { xs: 9, md: 4 }, flexGrow: 1 }}
        >
          {children}
        </Container>
      </Fade>

      {/* ── Mobile Bottom Navigation (xs/sm only) ──────────────────────── */}
      {(userRole === 'teacher' || userRole === 'admin') && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            display: { xs: 'block', md: 'none' },
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <BottomNavigation
            value={
              location.pathname === '/'
                ? 0
                : location.pathname === '/assistant'
                  ? 2
                  : location.pathname === '/today'
                    ? 3
                    : -1
            }
            showLabels
            sx={{ height: 64 }}
          >
            <BottomNavigationAction
              label={language === 'en' ? 'Home' : 'Главная'}
              icon={<DashboardIcon />}
              onClick={() => navigate('/')}
              sx={{ minWidth: 0 }}
            />
            <BottomNavigationAction
              label={language === 'en' ? 'Search' : 'Поиск'}
              icon={<SearchIcon />}
              onClick={openSearch}
              sx={{ minWidth: 0 }}
            />
            <BottomNavigationAction
              label="AI"
              icon={<SmartToyIcon />}
              onClick={() => navigate('/assistant')}
              sx={{
                minWidth: 0,
                '& .MuiBottomNavigationAction-label': {
                  color: orchard.honey,
                },
              }}
            />
            <BottomNavigationAction
              label={language === 'en' ? 'Today' : 'Сегодня'}
              icon={<TodayIcon />}
              onClick={() => navigate('/today')}
              sx={{ minWidth: 0 }}
            />
            {/* Tasks live in the drawer — the dot keeps overdue ones visible */}
            <BottomNavigationAction
              label={language === 'en' ? 'Menu' : 'Меню'}
              icon={
                <Badge
                  variant="dot"
                  color="error"
                  invisible={!overdueTaskCount && !inboxUnread}
                >
                  <MenuIcon />
                </Badge>
              }
              onClick={() => setDrawerOpen(true)}
              sx={{ minWidth: 0 }}
            />
          </BottomNavigation>
        </Paper>
      )}

      {roleNotice && (
        <Snackbar
          open
          autoHideDuration={roleNotice.severity === 'error' ? 5000 : 2500}
          onClose={(_event, reason) => {
            if (reason !== 'clickaway') setRoleNotice(null);
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ top: { xs: 68, sm: 76 } }}
        >
          <Alert
            severity={roleNotice.severity}
            variant="filled"
            onClose={() => setRoleNotice(null)}
            iconMapping={{
              success:
                userRole === 'admin' ? (
                  <AdminPanelSettingsIcon fontSize="inherit" />
                ) : (
                  <SchoolIcon fontSize="inherit" />
                ),
            }}
            sx={{ fontWeight: 700, borderRadius: 3 }}
          >
            {roleNotice.text}
          </Alert>
        </Snackbar>
      )}

      {/* Global Search */}
      <Dialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, mt: 8, verticalAlign: 'top' } }}
      >
        <DialogContent sx={{ p: 2 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder={
              language === 'en'
                ? 'Search students, templates… (Ctrl+K)'
                : 'Поиск учеников, шаблонов… (Ctrl+K)'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          {searchQuery.length > 0 && (
            <List dense sx={{ maxHeight: 420, overflow: 'auto' }}>
              {filteredStudents.length === 0 &&
              filteredTemplates.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      language === 'en'
                        ? 'No results found'
                        : 'Ничего не найдено'
                    }
                    primaryTypographyProps={{
                      color: 'text.secondary',
                      fontSize: '0.9rem',
                    }}
                  />
                </ListItem>
              ) : (
                <>
                  {filteredStudents.length > 0 && (
                    <>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={language === 'en' ? 'Students' : 'Ученики'}
                          primaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        />
                      </ListItem>
                      {filteredStudents.slice(0, 6).map((s) => (
                        <ListItem key={s._id} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              navigate(`/student/${s._id}`);
                              setSearchOpen(false);
                            }}
                            sx={{ borderRadius: 2 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <PersonIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={s.name}
                              secondary={
                                <span>
                                  {s.age
                                    ? `${language === 'en' ? 'Age' : 'Возраст'}: ${s.age}`
                                    : ''}
                                  {(s.tags || []).slice(0, 3).map((tag) => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      sx={{
                                        ml: 0.5,
                                        height: 16,
                                        fontSize: '0.6rem',
                                      }}
                                    />
                                  ))}
                                </span>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </>
                  )}
                  {filteredTemplates.length > 0 && (
                    <>
                      <ListItem sx={{ py: 0.5, mt: 0.5 }}>
                        <ListItemText
                          primary={language === 'en' ? 'Templates' : 'Шаблоны'}
                          primaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        />
                      </ListItem>
                      {filteredTemplates.slice(0, 4).map((tmpl) => (
                        <ListItem key={tmpl._id} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              navigate('/templates');
                              setSearchOpen(false);
                            }}
                            sx={{ borderRadius: 2 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <TemplateIcon
                                color="secondary"
                                fontSize="small"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={tmpl.name}
                              secondary={
                                tmpl.themes
                                  ? `${tmpl.themes.length} ${language === 'en' ? 'themes' : 'тем'}`
                                  : ''
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </>
                  )}
                </>
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
